<template>
  <div class="keyword-container">
    <div class="iframe-wrapper">
      <div v-if="isLoading || isError" class="iframe-overlay">
        <icon-loading v-if="isLoading" />
        <icon-error v-else-if="isError" />
      </div>

      <iframe ref="iframeRef" :src />
    </div>
  </div>
</template>

<script setup lang="ts">
import IconError from '@/components/icons/IconError.vue';
import IconLoading from '@/components/icons/IconLoading.vue';
import { useIframe } from '@/composables/shared/useIframe';
import { useThemeStore } from '@/stores/theme';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { TypeDomain } from '@/types/common';
import { IframeMessage } from '@/types/iframe';

const props = withDefaults(
  defineProps<{
    code: string;
    domain?: TypeDomain;
  }>(),
  {
    code: '',
    domain: 'CHAT',
  }
);

const emit = defineEmits<{ updated: [] }>();

const { t } = useI18n();

const themeStore = useThemeStore();
const toastStore = useToastStore();
const userStore = useUserStore();

const src = computed(() => {
  const origin = window.location.origin;
  const authToken = localStorage.getItem('authToken');
  const languageCode = userStore.pageLanguage;
  const isDarkMode = props.domain === 'SELF_SERVICE' ? false : themeStore.theme === 'dark';

  return `${origin}/admin/external/chat-keyword/${props.code}?authToken=${authToken}&languageCode=${languageCode}&isDarkMode=${isDarkMode}`;
});

const event_map = {
  CHAT_EDIT_SAVED: (data: IframeMessage) => {
    if (data.success) {
      toastStore.info(t('alert_save_completed'));
      emit('updated');
    } else if (data.error) {
      toastStore.error(data.error);
    }
  },
};

const handleIframeMessage = (data: IframeMessage) => {
  const event = event_map[data.type];
  event?.(data);
};

const { iframeRef, isLoading, isError } = useIframe('iframeRef', { onMessage: handleIframeMessage });
</script>

<style lang="scss" scoped>
.keyword-container {
  height: 100%;
  width: 27rem;
  overflow-y: auto;
  border-left: solid 1px $color-gray-40;
}

@include respond-to(mobile-landscape) {
  .keyword-container {
    display: none;
  }
}

.iframe-wrapper {
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;

  iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
}
</style>
